import React, { useCallback, useEffect } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import dayjs, { Dayjs } from 'dayjs';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { Alert, Breadcrumbs, BreadcrumbsItem, PageHeader, PageHeaderContent, PageTitle } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import { SubmitPaymentModal } from 'modals';
import { routeConstants, URL } from 'routes';
import { browserHistory } from 'routes/Router';
import { clearAllRequests, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCalculatorRules } from 'store/CalculatorRules';
import { selectEmployeePayrollTips } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { Payment, payPaymentSetRequest } from 'store/Payment';
import { selectSite, selectSiteAccountBalance } from 'store/Site/selectors';
import { resetCalculatorPaymentSet, selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults, selectCalculatorResults, updateCalculatorPaymentset } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { FeatureFlags, IDaySegment, IPayPeriod } from 'types';
import { buildPaymentSetRequest, buildPaymentWithholdings, calculateTipCalculatorPaymentTotals, centsToDollarsDisplay } from 'utils';
import { TipCalculatorProcessingModal } from './components/modals/TipCalculatorProcessingModal';
import { TipCalculatorProcessingModalMode } from './components/modals/TipCalculatorProcessingModal/types';
import { Checks, DateSelector, LaborData, OtherContributors, Pools, Review, TipData } from './steps';
import ChangeLog from './steps/ChangeLog';
import { CalculatorSteps, ICalculatorPaymentForm } from './types';

interface IParams {
  step: CalculatorSteps;
}

const makePaymentRoutes: string[] = [
  URL.MakePaymentCalculator,
  URL.MakePaymentCalculatorPools,
  URL.MakePaymentCalculatorOtherContributors,
  URL.MakePaymentCalculatorChecks,
  URL.MakePaymentCalculatorLaborData,
  URL.MakePaymentCalculatorTipAmounts,
  URL.MakePaymentCalculatorReview,
  URL.MakePaymentCalculatorChangeLog,
  `${URL.SettingsCalculator}/tip-shares`,
];

const PayCalculatorTips = () => {
  const { step } = useParams<IParams>();
  const dispatch = useDispatch();

  // Feature Flags
  const tipCalculatorEnabled = useFeatureFlags()[FeatureFlags.TIP_CALCULATOR]?.enabled;

  // Site State
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentSite?.id;
  const siteBalance = useSelector(selectSiteAccountBalance());
  const siteWithSettings = useSelector(selectSite());
  const payrollTips = useSelector(selectEmployeePayrollTips());

  // TipCalculator State
  const tipCalcResults = useSelector(selectCalculatorResults());
  const tipCalcPaymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = tipCalcPaymentSet?.segments;
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const changeLog = useSelector(selectCalculatorChangeLog());
  const posResults = useSelector(selectCalculatorPOSResults());

  // Component State
  const isCreatePaymentLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.CREATE_PAYMENT_REQUEST));

  useEffect(() => {
    if (!currentCalculatorRules || step !== CalculatorSteps.SELECT_DATE) browserHistory.push(routeConstants.private.makePayment);
    dispatch(resetCalculatorPaymentSet());
  }, []);

  const [showTipCalculatorProcessingModal, hideTipCalculatorProcessingModal] = useModal(
    () => (
      <TipCalculatorProcessingModal
        mode={TipCalculatorProcessingModalMode.INITIAL}
        tipCalcRules={currentCalculatorRules}
        onClose={hideTipCalculatorProcessingModal}
        businessDate={tipCalcPaymentSet.date}
        site={siteWithSettings}
        segments={paymentSetSegments}
        paymentSet={tipCalcPaymentSet}
      />
    ),
    [currentCalculatorRules, siteWithSettings, paymentSetSegments, tipCalcPaymentSet]
  );

  const onDateSelect = useCallback(
    (date: Dayjs, daySegments: IDaySegment[], payPeriod?: IPayPeriod) => {
      // update payment set & show processing modal
      dispatch(updateCalculatorPaymentset({ ...tipCalcPaymentSet, date: dayjs(date).format('YYYY-MM-DD'), siteId: currentSiteId, segments: daySegments, payPeriod }));
      dispatch(clearAllRequests());
      showTipCalculatorProcessingModal();
    },
    [tipCalcPaymentSet, currentSiteId]
  );

  const formik = useFormik({
    initialValues: {
      payees: [],
      description: '',
      authorizedAgreement: false,
      paymentDate: tipCalcPaymentSet?.date,
    } as ICalculatorPaymentForm,
    onSubmit: ({ payees, description, paymentDate }) => {
      dispatch(
        payPaymentSetRequest(
          buildPaymentSetRequest({
            method: currentCalculatorRules.method,
            tipCalcResults,
            changeLog,
            description,
            paymentDate,
            payees,
            site: siteWithSettings,
            posResults,
            daySegments: paymentSetSegments,
            withholdings: buildPaymentWithholdings(payees, siteWithSettings, tipCalcPaymentSet?.payPeriod, payrollTips?.data),
          }),
          hideSubmitPaymentModal
        )
      );
    },
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnChange: false,
  });

  const { paymentDate } = formik.values;
  const { values, setFieldValue, isValid, handleSubmit } = formik;

  const [showSubmitPaymentModal, hideSubmitPaymentModal] = useModal(
    () => (
      <SubmitPaymentModal
        values={values}
        totals={calculateTipCalculatorPaymentTotals(values.payees)}
        setFieldValue={setFieldValue}
        disabled={!isValid || !values.authorizedAgreement || !tipCalculatorEnabled}
        isLoading={isCreatePaymentLoading}
        onClose={hideSubmitPaymentModal}
        onSubmit={handleSubmit}
        isTipCalculator={true}
      />
    ),
    [values, setFieldValue, isValid, isCreatePaymentLoading, handleSubmit]
  );

  const pageHeaderContent = (
    <PageHeader data-cy="make-payment-header">
      <Breadcrumbs data-cy="payments-breadcrumbs">
        <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Payments" to={URL.PaymentsOverview} />
        <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Make Payment" to={URL.MakePayment} />
        <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Calculator" />
      </Breadcrumbs>

      <PageHeaderContent>
        <PageTitle title={currentSite?.name} subtitle={`Account Balance: ${siteBalance || siteBalance === 0 ? centsToDollarsDisplay(siteBalance) : '--'}`} />
      </PageHeaderContent>
    </PageHeader>
  );
  const goToReview = () => browserHistory.push(routeConstants.private.makePaymentCalculatorReview);

  if (!currentSite || !currentCalculatorRules) return null;

  return (
    <FormikProvider value={formik}>
      <FieldArray
        name="payees"
        render={() => (
          <>
            <NavigationPrompt
              when={(crntLocation, nextLocation) => {
                // when redirected after logout or changing organization
                if (nextLocation?.pathname === URL.Login || nextLocation?.pathname === URL.RootPrivate) return false;

                // when redirected after a successful submission
                if (nextLocation && nextLocation.pathname === URL.MakePaymentSummary) return false;

                // when following the date selection's link to review the labor for the selected day
                if (step === CalculatorSteps.SELECT_DATE && nextLocation?.pathname === URL.LaborReview) return false;

                return (!nextLocation || !makePaymentRoutes.includes(nextLocation.pathname)) && !!paymentDate;
              }}
            >
              {({ onConfirm, onCancel }) => (
                <Alert
                  dataCy="leave-payment-alert"
                  title="Leave make payment?"
                  message="Leaving this page will abandon all work. Are you sure you want to leave?"
                  onLeftAction={onCancel}
                  onRightAction={() => {
                    dispatch(resetCalculatorPaymentSet());
                    onConfirm();
                  }}
                  leftActionLabel="Cancel"
                  rightActionLabel="Leave"
                  isActive
                />
              )}
            </NavigationPrompt>

            {step === CalculatorSteps.SELECT_DATE && <DateSelector currentSite={currentSite} currentSiteId={currentSiteId} pageHeaderContent={pageHeaderContent} onNext={onDateSelect} />}

            {posResults && (
              <>
                {step === CalculatorSteps.REVIEW && <Review formik={formik} onNext={showSubmitPaymentModal} pageHeaderContent={pageHeaderContent} payrollTips={payrollTips?.data} site={siteWithSettings} />}

                {step === CalculatorSteps.POOLS && <Pools pageHeaderContent={pageHeaderContent} onBack={goToReview} />}

                {step === CalculatorSteps.OTHER_CONTRIBUTORS && <OtherContributors currentCalculatorRules={currentCalculatorRules} pageHeaderContent={pageHeaderContent} onBack={goToReview} />}

                {step === CalculatorSteps.CHECKS && <Checks currentCalculatorRules={currentCalculatorRules} site={siteWithSettings} pageHeaderContent={pageHeaderContent} onBack={goToReview} />}

                {step === CalculatorSteps.LABOR_DATA && <LaborData currentCalculatorRules={currentCalculatorRules} site={siteWithSettings} pageHeaderContent={pageHeaderContent} onBack={goToReview} />}

                {step === CalculatorSteps.TIP_DATA && <TipData currentCalculatorRules={currentCalculatorRules} site={siteWithSettings} pageHeaderContent={pageHeaderContent} onBack={goToReview} />}

                {step === CalculatorSteps.CHANGE_LOG && <ChangeLog site={siteWithSettings} pageHeaderContent={pageHeaderContent} onBack={goToReview} />}
              </>
            )}
          </>
        )}
      />
    </FormikProvider>
  );
};
export default PayCalculatorTips;
